import { DocumentTextIcon, ArrowUpTrayIcon, XMarkIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { DocumentIcon, PhotoIcon, FilmIcon, MusicalNoteIcon, ArchiveBoxIcon } from '@heroicons/react/24/solid';
import Card from 'components/Card';
import React, { useState } from 'react';
import { classNames } from 'components/Utils/ui'
import _ from 'lodash';
import Button from 'components/Form/Button';
import { FileUtils } from 'components/Utils/file';
import prettyBytes from 'pretty-bytes';

interface FileWithHash extends File {
    hash?: string;
    url?: string;
}

const getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase();

    // Images
    if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp'].includes(ext || '')) {
        return <PhotoIcon className="w-8 h-8 text-blue-500" />;
    }
    // Videos
    if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv'].includes(ext || '')) {
        return <FilmIcon className="w-8 h-8 text-purple-500" />;
    }
    // Audio
    if (['mp3', 'wav', 'ogg', 'flac', 'm4a'].includes(ext || '')) {
        return <MusicalNoteIcon className="w-8 h-8 text-pink-500" />;
    }
    // Archives
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext || '')) {
        return <ArchiveBoxIcon className="w-8 h-8 text-orange-500" />;
    }
    // Default
    return <DocumentIcon className="w-8 h-8 text-gray-500" />;
};

const MultiFileUploader = ({ files = [], onFilesSelected = (files) => { }, maxFileSizeMb = 100, isViewer = false }) => {
    const [isDragging, setIsDragging] = useState(false);

    const handleClick = async () => {
        const selectedFiles = (await FileUtils.selectFiles()) as FileWithHash[];
        console.log('[handleClick] selectedFiles', selectedFiles);
        if (!_.isArray(selectedFiles)) {
            return;
        }

        // 새로 선택한 파일들에만 해시 추가
        for (const file of selectedFiles) {
            if (!FileUtils.checkFileSize(file, maxFileSizeMb)) {
                return;
            }
            const hash = await FileUtils.getFileHash(file);
            file.hash = hash;
        }

        const newFiles = [...files, ...selectedFiles];
        onFilesSelected(newFiles);
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleDrop = async (event) => {
        event.preventDefault();
        setIsDragging(false);
        const droppedFiles = Array.from(event.dataTransfer.files) as FileWithHash[];
        console.log('[handleDrop] droppedFiles', droppedFiles);

        // 드롭된 파일들에만 해시 추가
        for (const file of droppedFiles) {
            if (!FileUtils.checkFileSize(file, maxFileSizeMb)) {
                return;
            }
            const hash = await FileUtils.getFileHash(file);
            file.hash = hash;
        }

        const newFiles = [...files, ...droppedFiles];
        onFilesSelected(newFiles);
    };

    const handleRemoveFile = (index: number) => {
        const newFiles = [...files];
        newFiles.splice(index, 1);
        onFilesSelected(newFiles);
    };

    const handleDownloadFile = (file: FileWithHash) => {
        const filename = file.name;
        const url = file.url;
        if (url) {
            FileUtils.downloadFile(url, filename);
        }
    };

    console.log('[MultiFileUploader] files', files);

    // Viewer Mode (Read-only)
    if (isViewer) {
        return (
            <div className="space-y-2">
                {files.map((file, index) => (
                    <div
                        key={index}
                        onClick={() => handleDownloadFile(file)}
                        className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 hover:border-gray-300 transition-all cursor-pointer group"
                    >
                        {getFileIcon(file.name)}
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                                {file.name}
                            </p>
                            <p className="text-xs text-gray-500">
                                {prettyBytes(file.size)}
                            </p>
                        </div>
                        <ArrowDownTrayIcon className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
                    </div>
                ))}
                {_.isEmpty(files) && (
                    <div className="text-center py-8 text-gray-500 text-sm">
                        첨부된 파일이 없습니다
                    </div>
                )}
            </div>
        );
    }

    // Upload Mode
    return (
        <div className="space-y-4">
            {/* Dropzone */}
            <div
                className={classNames(
                    'relative border-2 border-dashed rounded-xl transition-all duration-200 cursor-pointer',
                    isDragging
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50',
                    _.isEmpty(files) ? 'py-12' : 'py-6'
                )}
                onClick={handleClick}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <div className="text-center pointer-events-none">
                    {_.isEmpty(files) ? (
                        <>
                            <ArrowUpTrayIcon className="mx-auto h-12 w-12 text-gray-400" />
                            <div className="mt-4 flex justify-center text-sm leading-6 text-gray-600">
                                <span className="font-semibold text-blue-600">파일 선택</span>
                                <p className="pl-1">또는 드래그하여 업로드</p>
                            </div>
                            <p className="text-xs leading-5 text-gray-500 mt-2">
                                최대 {maxFileSizeMb}MB
                            </p>
                        </>
                    ) : (
                        <div className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600">
                            <ArrowUpTrayIcon className="w-4 h-4" />
                            파일 추가
                        </div>
                    )}
                </div>
            </div>

            {/* File List */}
            {!_.isEmpty(files) && (
                <div className="space-y-2">
                    {files.map((file, index) => (
                        <div
                            key={index}
                            className={`flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-all group ${file.url ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                            onClick={() => file.url && handleDownloadFile(file)}
                        >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                {getFileIcon(file.name)}
                                <div className="flex-1 min-w-0">
                                    <p className={`text-sm font-medium truncate ${file.url ? 'text-blue-600 group-hover:text-blue-700' : 'text-gray-900'}`}>
                                        {file.name}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        {prettyBytes(file.size)}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={(e) => { e.stopPropagation(); handleRemoveFile(index); }}
                                className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                                title="삭제"
                            >
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MultiFileUploader;
